if (!customElements.get('product-information')) {
  customElements.define('product-information', class ProductInformation extends HTMLElement {
    staticBuyTools = true;
    quantityInput = undefined;
    onVariantChangeUnsubscriber = undefined;
    cartUpdateUnsubscriber = undefined;
    abortController = undefined;
    pendingRequestUrl = null;
    preProcessHtmlCallbacks = [];
    postProcessHtmlCallbacks = [];

    constructor() {
      super();
      this.buyToolsTrigger = this.querySelector('#product__buy-tools-trigger');
      this.productInfoContainer = this.querySelector('.product__info-container');
      this.onScrollToolsHandler = this.onScrollBuyTools.bind(this);
      this.quantityInput = this.querySelector(`#QuantityInput-${this.sectionId}`);

      this.initBreakpoints();
    }

    initBreakpoints() {
      const self = this;

      enquire.register('screen and (max-width: 1000px)', {
        match: () => {
          self.bpSmall = true;
          this.productInfoContainer.classList.remove('product__info-container--modal--out');
          this.productInfoContainer.classList.remove('product__info-container--modal', 'background-modal');
        },
        unmatch: () => {
          self.bpSmall = false;
          this.productInfoContainer.classList.remove('product__info-container--modal--out');
          this.productInfoContainer.classList.remove('product__info-container--modal', 'background-modal');
        }
      });
    }

    connectedCallback() {
      this.initializeProductSwapUtility();

      this.onVariantChangeUnsubscriber = subscribe(
        PUB_SUB_EVENTS.optionValueSelectionChange,
        this.handleOptionValueChange.bind(this)
      );
    }

    addPreProcessCallback(callback) {
      this.preProcessHtmlCallbacks.push(callback);
    }

    disconnectedCallback() {
      this.onVariantChangeUnsubscriber?.();
      this.abortController?.abort();
      this.resizeProductInfoObserver?.disconnect();
      // this.cartUpdateUnsubscriber?.();
    }

    initializeProductSwapUtility() {
      this.preProcessHtmlCallbacks.push((html) => {
        html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'));
      });
      this.postProcessHtmlCallbacks.push((newNode) => {
        window?.Shopify?.PaymentButton?.init();
        window?.ProductModel?.loadShopifyXR();
      });
    }

    handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
      if (!this.contains(event.target)) return;

      this.resetProductFormState();

      const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
      this.pendingRequestUrl = productUrl;
      const shouldSwapProduct = this.dataset.url !== productUrl;
      const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

      this.renderProductInfo({
        requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
        targetId: target.id,
        callback: shouldSwapProduct
          ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
          : this.handleUpdateProductInfo(productUrl),
      });
    }

    resetProductFormState() {
      const productForm = this.productForm;
      this.toggleSubmitButton(true);
      productForm?.handleErrorMessage();
    }

    handleSwapProduct(productUrl, updateFullPage) {
      return (html) => {
        this.productModal?.remove();

        const selector = updateFullPage ? "product-information[id^='MainProduct']" : 'product-information';
        const variant = this.getSelectedVariant(html.querySelector(selector));
        this.updateURL(productUrl, variant?.id);

        if (updateFullPage) {
          document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

          HTMLUpdateUtility.viewTransition(
            document.querySelector('main'),
            html.querySelector('main'),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );
        } else {
          HTMLUpdateUtility.viewTransition(
            this,
            html.querySelector('product-information'),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );
        }
      };
    }

    renderProductInfo({ requestUrl, targetId, callback }) {
      this.abortController?.abort();
      this.abortController = new AbortController();

      fetch(requestUrl, { signal: this.abortController.signal })
        .then((response) => {
          if (!response.ok) throw new Error(`Product update request failed with ${response.status}`);
          return response.text();
        })
        .then((responseText) => {
          this.pendingRequestUrl = null;
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          if (!html.querySelector('product-information')) {
            throw new Error('Product update response did not contain product information.');
          }
          callback(html);
        })
        .then(() => {
          // set focus to last clicked option value
          document.querySelector(`#${targetId}`)?.focus();
        })
        .catch((error) => {
          if (error.name !== 'AbortError') {
            console.error(error);
          }
        });
    }

    getSelectedVariant(productInfoNode) {
      if (!productInfoNode) return null;
      const selector = 'variant-selects [data-selected-variant]';
      const selectedVariant = productInfoNode.querySelector(selector)?.textContent;
      if (!selectedVariant) return null;
      try {
        return JSON.parse(selectedVariant);
      } catch (error) {
        console.error('Unable to parse selected product variant.', error);
        return null;
      }
    }

    buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
      const params = [];

      !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

      if (optionValues.length) {
        params.push(`option_values=${optionValues.join(',')}`);
      }

      return `${url}?${params.join('&')}`;
    }

    updateOptionValues(html) {
      const sectionId = `${this.sectionId}${this.blockId ? `${this.blockId}`: ''}`;
      const variantSelects = html.getElementById(`VariantSelect-${sectionId}`);

      if (variantSelects) {
        const isOnProductModal =
          this.closest('complementary-products-card') ||
          this.closest('quick-add-modal') ||
          this.closest('.shop-look__item-wrap');
        if (isOnProductModal) {
          const updatedOptions = variantSelects.querySelectorAll('fieldset');

          this.variantSelectors.querySelectorAll('input[data-disabled="true"]').forEach((i) => {
            i.removeAttribute('data-disabled');
          });

          updatedOptions.forEach((option) => {
            option.querySelectorAll('input[data-disabled="true"]').forEach((i) => {
              const inputId = i.dataset.optionValueId;
              const currentNode = this.variantSelectors.querySelector(`input[data-option-value-id="${inputId}"]`);
              if (currentNode?.tagName === 'INPUT' && currentNode?.type === 'radio') {
                currentNode.setAttribute('data-disabled', 'true');
              }
            });
          });
        } else if (!this.updateOptionValuesInPlace(variantSelects)) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }
    }

    // Sync the existing picker instead of swapping its markup, so the option
    // buttons don't redraw on every variant change. Returns false when the new
    // markup doesn't line up with what's on the page, so the caller can fall
    // back to replacing it wholesale.
    updateOptionValuesInPlace(variantSelects) {
      const currentSelectors = this.variantSelectors;
      if (!currentSelectors) return false;

      // Dropdown and checkbox pickers carry state we don't sync here.
      if (variantSelects.querySelector('select') || currentSelectors.querySelector('select')) return false;
      if (variantSelects.querySelector('input[type="checkbox"]')) return false;

      const updatedInputs = variantSelects.querySelectorAll('input[type="radio"][data-option-value-id]');
      if (!updatedInputs.length) return false;
      if (updatedInputs.length !== currentSelectors.querySelectorAll('input[type="radio"][data-option-value-id]').length) {
        return false;
      }

      const pairs = [];

      for (const updatedInput of updatedInputs) {
        const currentInput = currentSelectors.querySelector(
          `input[type="radio"][data-option-value-id="${updatedInput.dataset.optionValueId}"]`
        );
        // An option value we don't already have on the page means the whole
        // picker changed shape - let the caller replace it.
        if (!currentInput) return false;
        pairs.push([currentInput, updatedInput]);
      }

      pairs.forEach(([currentInput, updatedInput]) => {
        if (updatedInput.hasAttribute('data-disabled')) {
          currentInput.setAttribute('data-disabled', 'true');
        } else {
          currentInput.removeAttribute('data-disabled');
        }

        if (updatedInput.dataset.productUrl !== undefined) {
          currentInput.setAttribute('data-product-url', updatedInput.dataset.productUrl);
        }
      });

      // Swatch legends print the selected value as text.
      const updatedSelectedValues = variantSelects.querySelectorAll('[data-selected-value]');
      const currentSelectedValues = currentSelectors.querySelectorAll('[data-selected-value]');
      if (updatedSelectedValues.length === currentSelectedValues.length) {
        currentSelectedValues.forEach((node, index) => {
          node.textContent = updatedSelectedValues[index].textContent;
        });
      }

      // Keep the embedded variant JSON current for anything that reads it later.
      const updatedVariantJson = variantSelects.querySelector('script[data-selected-variant]');
      const currentVariantJson = currentSelectors.querySelector('script[data-selected-variant]');
      if (updatedVariantJson && currentVariantJson) {
        currentVariantJson.textContent = updatedVariantJson.textContent;
      }

      return true;
    }

    handleUpdateProductInfo(productUrl) {
      return (html) => {
        const variant = this.getSelectedVariant(html);
        const sectionId = `${this.sectionId}${this.blockId ? `${this.blockId}`: ''}`;

        this.pickupAvailability?.update(variant);
        this.updateOptionValues(html);
        this.updateURL(productUrl, variant?.id);
        this.updateVariantInputs(variant?.id);

        if (!variant) {
          this.setUnavailable();
          return;
        }

        this.updateMedia(variant, variant?.featured_media?.id);

        const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
          const source = html.getElementById(`${id}-${sectionId}`);
          const destination = this.querySelector(`#${id}-${this.dataset.section}`);
          if (source && destination) {
            destination.innerHTML = source.innerHTML;
            destination.classList.toggle('visibility-hidden', shouldHide(source));
          }
        };

        updateSourceFromDestination('price');
        updateSourceFromDestination('sku', ({ classList }) => classList.contains('visibility-hidden'));
        updateSourceFromDestination('StockNotification', ({ innerText }) => innerText === '');

        this.updateLimitQuantity(this.sectionId, html);

        this.toggleSubmitButton(
          html.getElementById(`ProductSubmitButton-${sectionId}`)?.hasAttribute('disabled') ?? true,
          window.variantStrings.soldOut
        );

        publish(PUB_SUB_EVENTS.variantChange, {
          data: {
            sectionId: this.sectionId,
            html,
            variant,
          },
        });
      };
    }

    updateLimitQuantity(sectionId, html) {
      if (!this.quantityInput) return;
      const quantityFormUpdated = html.getElementById(`QuantityInput-${sectionId}`);

      if (quantityFormUpdated) this.quantityInput.updateLimitQuantity(quantityFormUpdated);
    }

    updateVariantInputs(variantId) {
      const sectionId = `${this.dataset.section}`;

      this.querySelectorAll(
        `#product-form-${sectionId}, #product-form-installment-${sectionId}`
      ).forEach((productForm) => {
        const input = productForm.querySelector('input[name="id"]');
        if (!input) return;
        input.value = variantId ?? '';
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    updateURL(url, variantId) {
      this.querySelector('share-button')?.updateUrl(
        `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
      );

      if (this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
    }

    toggleSubmitButton(disable = true, text) {
      const productForm = this.productForm?.querySelector('form');
      if (!productForm) return;

      const addButton = productForm.querySelector('[data-add-to-cart]');
      const addButtonText = productForm.querySelector('[data-add-to-cart-text]');
      if (!addButton) return;

      const variantClassSoldOut = 'product-form--variant-sold-out';
      const formParentElement = productForm.closest('.product-form__buy-buttons');
      const productMetaPrice = formParentElement ? formParentElement.querySelector('[data-product-meta-price]') : null;

      if (disable) {
        addButton.setAttribute('aria-disabled', true);
        addButton.setAttribute('disabled', true);
        addButton.setAttribute('aria-label', text);
        if (text) {
          addButtonText.textContent = text;
          productForm.classList.add(variantClassSoldOut);
          if (productMetaPrice) productMetaPrice.classList.add('meta-price--hidden');
        }
      } else {
        addButton.removeAttribute('aria-disabled');
        addButton.removeAttribute('disabled');
        addButton.setAttribute('aria-label', window.variantStrings.addToCart);
        addButtonText.textContent = window.variantStrings.addToCart;
        productForm.classList.remove(variantClassSoldOut);
        if (productMetaPrice) productMetaPrice.classList.remove('meta-price--hidden');
      }
    }

    setUnavailable() {
      this.toggleSubmitButton(true, window.variantStrings.unavailable);

      const selectors = ['price', 'sku', 'StockNotification']
        .map((id) => `#${id}-${this.dataset.section}`)
        .join(', ');

      document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('visibility-hidden'));
    }

    updateMedia(variant, variantFeaturedMediaId) {
      const sectionId = `${this.dataset.section}`;
      const mediaGallery = this.querySelector(`#MediaGallery-${sectionId}`);
      if (!mediaGallery) return;

      if (variantFeaturedMediaId) {
        mediaGallery.setActiveMedia(`${sectionId}-${variantFeaturedMediaId}`, variant);
        return;
      }

      // The variant has no image of its own. The gallery still filters images by the
      // "(Value)" tag in their alt text, so re-filter whenever a swatch option
      // (Colour, Style, Baby…) changes. Size-only changes leave the gallery where it is.
      if (!variant || mediaGallery.dataset.enableAllImages !== 'false') return;
      const swatches = mediaGallery.productSwatches.map((name) => name.trim().toLowerCase());
      const swatchValue = mediaGallery.productOptions
        .map((name, index) => (swatches.includes(name.trim().toLowerCase()) ? variant.options[index] : null))
        .filter(Boolean)
        .join('|');
      if (swatchValue === this.lastSwatchValue) return;
      this.lastSwatchValue = swatchValue;
      mediaGallery.setActiveMedia(null, variant);
    }

    buyToolsCalculations() {
      this.staticBuyTools = false;
      this.productPageSection = this.parentElement;
      this.productInfoWrapper = this.productPageSection.querySelector('.product__info-wrapper');
      this.productInfoContainerHeight = this.productInfoContainer.offsetHeight;
      this.negativeHeight = this.productInfoContainerHeight * -1;
      this.productContentSection = document.querySelector('.product-content-section');

      const productContentMinHeight = this.productInfoContainerHeight - this.productPageSection.offsetHeight + 54;
      this.productContentSection.style.minHeight = `${productContentMinHeight}px`;

      this.resizeProductInfoObserver = new ResizeObserver((entries) => {
        if (this.bpSmall) return;
        if (!this.productInfoContainer.classList.contains('product__info-container--modal')) {
          const productInfoContainerHeight = this.productInfoContainer.offsetHeight;
          if (productInfoContainerHeight != this.productInfoContainerHeight) {
            this.productInfoContainerHeight = productInfoContainerHeight;
            const productContentMinHeight = productInfoContainerHeight - this.productPageSection.offsetHeight + 54;
            this.productContentSection.style.minHeight = `${productContentMinHeight}px`;

            this.negativeHeight = productInfoContainerHeight * -1;
          }
        }
      });

      this.resizeProductInfoObserver.observe(this.productInfoContainer);
    }

    onScrollBuyTools() {
      if (this.bpSmall || !this.buyToolsTrigger ) return;

      const triggerTop = this.buyToolsTrigger.getBoundingClientRect().top;
      const buyToolsModalActive = this.productInfoContainer.classList.contains('product__info-container--modal', 'background-modal');
      const bytToolsModalOut = this.productInfoContainer.classList.contains('product__info-container--modal--out');

      if (triggerTop < this.negativeHeight && !buyToolsModalActive) {
        requestAnimationFrame(() => {
          this.productInfoContainer.classList.add('product__info-container--modal', 'background-modal');
        });
      } else if (triggerTop > this.negativeHeight && buyToolsModalActive && !bytToolsModalOut) {
        requestAnimationFrame(() => {
          this.productInfoContainer.classList.add('product__info-container--modal--out');

          this.productInfoContainer.addEventListener('animationend', () => {
            this.productInfoContainer.classList.remove('product__info-container--modal--out');
            this.productInfoContainer.classList.remove('product__info-container--modal', 'background-modal');
          }, { once: true });
        });
      }
    }

    get productForm() {
      return this.querySelector(`product-form`);
    }

    get productModal() {
      return document.querySelector(`#ProductModal-${this.sectionId}`);
    }

    get pickupAvailability() {
      return this.querySelector(`pickup-availability`);
    }

    get variantSelectors() {
      return this.querySelector('variant-selects');
    }

    get relatedProducts() {
      const relatedProductsSectionId = SectionId.getIdForSection(
        SectionId.parseId(this.sectionId),
        'related-products'
      );
      return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
    }

    get sectionId() {
      return this.dataset.originalSection || this.dataset.section;
    }

    get blockId() {
      return this.dataset.block;
    }

    get productId() {
      return this.dataset.productId;
    }
  });
}
